import { validateOrFail as validateOrFailCnpj } from 'validation-br/dist/cnpj'
import { validateOrFail as validateOrFailCpf } from 'validation-br/dist/cpf'
import * as yup from 'yup'

import { getCEPData } from 'utils/cepService'
import { normalizeDocument } from 'utils/document'

const validatePostalCode = async (
  value: string,
  values: yup.TestContext<any>
) => {
  try {
    const response = await getCEPData(value)

    if (response.error) {
      return values.createError({
        message: response.message || 'CEP inválido',
        path: 'addresses_attributes.postal_code'
      })
    }

    return true
  } catch (e) {
    return true
  }
}

const customerFieldsSchema = yup.object({
  uuid: yup.string(),
  field_label: yup.string().nullable(),
  field_value: yup.string().nullable()
})

export type CustomerFieldsSchema = yup.InferType<typeof customerFieldsSchema>

export const customerFormSchema = yup.object({
  foreign: yup.boolean(),
  country_code: yup.string().nullable(),
  name: yup.string().required('Nome do cliente é obrigatório.'),
  nickname: yup.string(),
  tributary_regime: yup.string().nullable(),
  municipal_registration: yup.string().nullable(),
  document: yup
    .string()
    .required('Documento é obrigatório.')
    .when('foreign', {
      is: true,
      then: (schema) => schema,
      otherwise: (schema) =>
        schema.test('cpfCnpj', 'CPF/CNPJ inválido', function (value?: string) {
          const { path, createError } = this
          if (!value) return true
          try {
            const onlyDigits = normalizeDocument(value)
            if (onlyDigits.length === 11) {
              validateOrFailCpf(onlyDigits)
            } else {
              validateOrFailCnpj(onlyDigits)
            }
            return true
          } catch {
            return createError({ message: 'CPF/CNPJ inválido', path })
          }
        })
    }),
  email: yup
    .string()
    .nullable()
    .matches(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, {
      message: 'O e-mail informado contém caracteres inválidos.',
      excludeEmptyString: true
    }),
  phone: yup.string(),
  category: yup.string(),
  customer_fields: yup.array().of(customerFieldsSchema),
  tags: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required('Identificador da tag é obrigatório.'),
        name: yup.string().required('Nome da tag é obrigatório.'),
        color: yup.string()
      })
    )
    .optional(),
  addresses_attributes: yup.object({
    street: yup.string().nullable(),
    number: yup.string().nullable(),
    city: yup.string().nullable(),
    state: yup
      .object({
        label: yup.string().nullable(),
        value: yup.string().nullable()
      })
      .nullable(),
    postal_code: yup
      .string()
      .nullable()
      .test('postalCode', async function (value) {
        if (!value || value.trim() === '') return true
        const fromArr = (this as any).from as Array<{ value: any }> | undefined
        const parent = fromArr?.[fromArr.length - 1]?.value
        if (parent?.foreign) return true
        return validatePostalCode(value, this)
      }),
    complement: yup.string().nullable(),
    neighborhood: yup.string().nullable()
  }),
  // Internal identifiers validation
  hasIdentifyInternal: yup.boolean(),
  internalIdentifiers: yup
    .array()
    .of(
      yup.object().shape({
        name: yup
          .string()
          .required('Preencha o nome do identificador interno.'),
        value: yup
          .string()
          .required('Preencha o valor do identificador interno.')
      })
    )
    .when('hasIdentifyInternal', {
      is: true,
      then: (schema) =>
        schema.min(1, 'Adicione pelo menos um identificador interno.'),
      otherwise: (schema) => schema
    })
    .default([])
})

export type CustomerFormSchema = yup.InferType<typeof customerFormSchema>
